import React from "react";
import { Grid, Table, Header } from "semantic-ui-react";

export const INDIA_PRINT_PAGE_STYLE = `
  @page {
    size: A4;
    margin: 0;
  }
  html, body {
    margin: 0;
    padding: 0;
  }
  .india-pdf {
    padding: 12mm !important;
    box-sizing: border-box;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .india-pdf thead {
    display: table-header-group !important;
  }
  .india-pdf .pdf-table-lead-in {
    height: 12mm !important;
  }
  .india-pdf .pdf-items-table {
    margin-top: -12mm !important;
  }
  .india-pdf .pdf-continued-page-spacer,
  .india-pdf .pdf-continued-page-spacer > th,
  .india-pdf .pdf-continued-page-spacer > td {
    height: 12mm !important;
    min-height: 12mm !important;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    background-color: transparent !important;
    box-shadow: none !important;
    font-size: 0 !important;
    line-height: 0 !important;
  }
  .india-pdf .pdf-table-header {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .india-pdf .pdf-work-name,
  .india-pdf .pdf-bank-details,
  .india-pdf .pdf-bank-details-row {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    white-space: pre-wrap !important;
    overflow-wrap: anywhere !important;
    word-break: break-word !important;
    overflow: visible !important;
    max-height: none !important;
  }
`;

export const suppressIndiaPrintChrome = () => {
  const iframe = document.getElementById("printWindow");
  if (iframe && iframe.contentDocument) {
    iframe.contentDocument.title = "";
  }
};

export const EMPTY_BANK_DETAILS = {
  accountHolderName: "",
  bankName: "",
  accountNumber: "",
  iban: "",
  swiftBic: "",
  branch: "",
  bankAddress: "",
  country: "",
  currency: "",
  routingNumber: "",
};

export const BANK_DETAIL_FIELDS = [
  { key: "accountHolderName", label: "Account Holder Name" },
  { key: "bankName", label: "Bank Name" },
  { key: "accountNumber", label: "Account Number" },
  { key: "iban", label: "IBAN" },
  { key: "swiftBic", label: "SWIFT / BIC" },
  { key: "branch", label: "Branch" },
  { key: "bankAddress", label: "Bank Address" },
  { key: "country", label: "Country" },
  { key: "currency", label: "Currency" },
  { key: "routingNumber", label: "Routing Number" },
];

export const parseBankDetails = (raw) => {
  if (!raw) {
    return { ...EMPTY_BANK_DETAILS };
  }
  let parsed = raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) {
      return { ...EMPTY_BANK_DETAILS };
    }
    try {
      parsed = JSON.parse(trimmed);
    } catch (e) {
      return { ...EMPTY_BANK_DETAILS };
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ...EMPTY_BANK_DETAILS };
  }
  return {
    accountHolderName: parsed.accountHolderName || "",
    bankName: parsed.bankName || "",
    accountNumber: parsed.accountNumber || "",
    iban: parsed.iban || "",
    swiftBic: parsed.swiftBic || "",
    branch: parsed.branch || "",
    bankAddress: parsed.bankAddress || "",
    country: parsed.country || "",
    currency: parsed.currency || "",
    routingNumber: parsed.routingNumber || "",
  };
};

export const hasBankDetails = (details) =>
  Object.values(details || {}).some(
    (value) => String(value || "").trim() !== ""
  );

const getPrintableBankRows = (dataObject) => {
  const details =
    dataObject?.bankDetails && typeof dataObject.bankDetails === "object"
      ? { ...EMPTY_BANK_DETAILS, ...dataObject.bankDetails }
      : parseBankDetails(dataObject?.additionalInfo);
  return BANK_DETAIL_FIELDS.map((field) => ({
    ...field,
    value: String(details[field.key] || "").trim(),
  })).filter((field) => field.value !== "");
};

const InvoiceIndiaPrintView = React.forwardRef(function InvoiceIndiaPrintView(
  { dataObject },
  ref
) {
  const textColor = "#6e6e6e";
  const headerBg = "#e6e6e6";
  const bankRows = getPrintableBankRows(dataObject);
  const companyName =
    dataObject?.companyName || dataObject?.to || dataObject?.invoiceName || "";
  const contactMobile =
    dataObject?.customerContact || dataObject?.mobile || "";
  const email = dataObject?.email || dataObject?.emailId || "";
  const headerStyle = {
    backgroundColor: headerBg,
    color: textColor,
    fontWeight: "bold",
    borderColor: "#d0d0d0",
    WebkitPrintColorAdjust: "exact",
    printColorAdjust: "exact",
  };
  const stylesForPrint = `
  @media print {
    ${INDIA_PRINT_PAGE_STYLE}
    body {
      margin: 0;
    }
    .india-pdf {
      padding: 12mm !important;
      box-sizing: border-box;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .india-pdf .pdf-table-header {
      background-color: ${headerBg} !important;
      color: ${textColor} !important;
      border-color: #d0d0d0 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .india-pdf .pdf-continued-page-spacer,
    .india-pdf .pdf-continued-page-spacer > th,
    .india-pdf .pdf-continued-page-spacer > td {
      border: none !important;
      background: transparent !important;
      background-color: transparent !important;
    }
    table {
      page-break-inside: auto;
    }
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    thead {
      display: table-header-group !important;
    }
  }
`;

  return (
    <div
      ref={ref}
      className="india-pdf"
      style={{
        width: "210mm",
        padding: 30,
        backgroundColor: "#ffffff",
        boxSizing: "border-box",
      }}
    >
      <style>{stylesForPrint}</style>
      <Grid>
        <Grid.Column width={5} floated="left">
          <Header as="h3">
            PARMAR GROUP <br />
            <Header.Subheader as="span">SILVER STAR</Header.Subheader>
          </Header>
        </Grid.Column>
        <Grid.Column width={7}>
          <Header as="h3" textAlign="right">
            INVOICE
          </Header>
          <Table style={{ border: "none" }}>
            <Table.Body>
              <Table.Row className="noBottomBorder">
                <Table.Cell textAlign="left">Date</Table.Cell>
                <Table.Cell textAlign="right">{dataObject?.date}</Table.Cell>
              </Table.Row>
              <Table.Row className="noBottomBorder">
                <Table.Cell textAlign="left">Customer Name</Table.Cell>
                <Table.Cell textAlign="right">{dataObject?.to}</Table.Cell>
              </Table.Row>
              <Table.Row className="noBottomBorder">
                <Table.Cell textAlign="left">Company Name</Table.Cell>
                <Table.Cell textAlign="right">{companyName}</Table.Cell>
              </Table.Row>
              <Table.Row className="noBottomBorder">
                <Table.Cell textAlign="left">Mobile</Table.Cell>
                <Table.Cell textAlign="right">
                  {dataObject?.customerContact}
                </Table.Cell>
              </Table.Row>
              <Table.Row className="noBottomBorder">
                <Table.Cell textAlign="left">Contact Person Mobile</Table.Cell>
                <Table.Cell textAlign="right">{contactMobile}</Table.Cell>
              </Table.Row>
              <Table.Row className="noBottomBorder">
                <Table.Cell textAlign="left">Email ID</Table.Cell>
                <Table.Cell textAlign="right">{email}</Table.Cell>
              </Table.Row>
              <Table.Row className="noBottomBorder">
                <Table.Cell textAlign="left">Invoice NO</Table.Cell>
                <Table.Cell textAlign="right">
                  {dataObject?.invoiceNumber}
                </Table.Cell>
              </Table.Row>
              <Table.Row className="noBottomBorder">
                <Table.Cell textAlign="left">Person Name</Table.Cell>
                <Table.Cell textAlign="right">
                  {dataObject?.personName}
                </Table.Cell>
              </Table.Row>
              <Table.Row className="noBottomBorder">
                <Table.Cell textAlign="left">Work Name</Table.Cell>
                <Table.Cell textAlign="right">
                  <div
                    className="pdf-work-name"
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      overflow: "visible",
                      maxHeight: "none",
                      textAlign: "right",
                    }}
                  >
                    {dataObject?.workName}
                  </div>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </Grid.Column>
      </Grid>
      <div
        style={{
          width: "100%",
          marginTop: "8px",
          marginBottom: "8px",
          overflow: "visible",
        }}
      >
        {bankRows.length > 0 && (
          <div
            className="pdf-bank-details"
            style={{
              width: "100%",
              marginTop: "8px",
              color: textColor,
              fontSize: "12px",
              lineHeight: 1.45,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              overflow: "visible",
              maxHeight: "none",
            }}
          >
            <div
              style={{
                fontWeight: "bold",
                marginBottom: "4px",
                color: textColor,
              }}
            >
              Bank Details
            </div>
            {bankRows.map((row) => (
              <div
                key={row.key}
                className="pdf-bank-details-row"
                style={{
                  width: "100%",
                  marginBottom: "2px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  overflow: "visible",
                }}
              >
                <span style={{ fontWeight: 600 }}>{row.label}: </span>
                <span>{row.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="pdf-table-lead-in" />
      <Table className="pdf-items-table">
        <Table.Header>
          <Table.Row className="pdf-continued-page-spacer">
            <Table.HeaderCell colSpan={6}>{'\u00a0'}</Table.HeaderCell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell className="pdf-table-header" style={headerStyle}>
              Attractions
            </Table.HeaderCell>
            <Table.HeaderCell className="pdf-table-header" style={headerStyle}>
              Description
            </Table.HeaderCell>
            <Table.HeaderCell className="pdf-table-header" style={headerStyle}>
              Qty
            </Table.HeaderCell>
            <Table.HeaderCell className="pdf-table-header" style={headerStyle}>
              Price
            </Table.HeaderCell>
            <Table.HeaderCell className="pdf-table-header" style={headerStyle}>
              Recived Money
            </Table.HeaderCell>
            <Table.HeaderCell className="pdf-table-header" style={headerStyle}>
              Total
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(dataObject?.rowData || []).map((item, index) => (
            <Table.Row key={index}>
              <Table.Cell style={{ padding: "5px" }}>
                {item?.itemName || item?.allAttractions || ""}
              </Table.Cell>
              <Table.Cell style={{ padding: "5px" }}>
                {item?.itemDescription}
              </Table.Cell>
              <Table.Cell style={{ padding: "5px" }}>{item?.itemQty}</Table.Cell>
              <Table.Cell style={{ padding: "5px" }}>
                {item?.unitPrice}
              </Table.Cell>
              <Table.Cell style={{ padding: "5px" }}>{item?.disPer}</Table.Cell>
              <Table.Cell style={{ padding: "5px" }}>{item?.amount}</Table.Cell>
            </Table.Row>
          ))}
          <Table.Row>
            <Table.Cell style={{ padding: "5px" }}></Table.Cell>
            <Table.Cell style={{ padding: "5px" }}></Table.Cell>
            <Table.Cell style={{ padding: "5px" }}></Table.Cell>
            <Table.Cell style={{ padding: "5px" }}></Table.Cell>
            <Table.Cell style={{ padding: "5px" }}>
              <b>
                <h3>Total</h3>
              </b>
            </Table.Cell>
            <Table.Cell style={{ padding: "5px" }}>
              <b>
                <h3>{dataObject?.totalAmount}</h3>
              </b>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
      <div
        style={{
          backgroundColor: "#f0f0f0",
          width: "100%",
          textAlign: "center",
          padding: "10px",
          marginTop: "20px",
        }}
      >
        <h4> Thank you for your business</h4>
      </div>
    </div>
  );
});

InvoiceIndiaPrintView.displayName = "InvoiceIndiaPrintView";

export default InvoiceIndiaPrintView;
